import React, { FC, useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faPen, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons'
import { useAppDispatch } from 'src/redux/storeHooks'
import TableLoadingShell from 'src/utils/TableLoadingShell'
import { getAllVariantsAsync } from 'src/redux/actions/omsProductActions'
import './ProductVariantsModal.scss'
import { KTIcon } from 'src/marketConnect/helpers'

type ListingRow = {
  id: number
  name?: string
  sku?: string
  [key: string]: any
}

type OptionValue = {
  id?: number
  value?: string
  api_id?: string
  [key: string]: any
} | null

type VariantItem = {
  id: number
  name?: string
  sku?: string
  barcode?: string
  total_available_quantity?: number
  total_reserve_quantity?: number
  platform_product_option_value1?: OptionValue
  platform_product_option_value2?: OptionValue
  platform_product_option_value3?: OptionValue
  price?: {
    type?: string
    value?: number | string
    currency_iso?: string | null
    id?: number
  } | null
  compare_price?: {
    type?: string
    value?: number | string
    currency_iso?: string | null
    id?: number
  } | null
  image?: {
    image_url?: string
    id?: number
  } | null
  created_at?: string
  updated_at?: string
  [key: string]: any
}

type ProductVariantsApiResponse = {
  status?: number
  message?: string
  data?: {
    id?: number
    variants?: VariantItem[]
  }
  errors?: any
  headers?: any
}

type ProductVariantsModalProps = {
  show: boolean
  onClose: () => void
  product: ListingRow | null
  isOmsStore?: boolean
  platformId?: number | string
  goToAddVariant?: (product: ListingRow) => void
  onEditVariant?: (variant: VariantItem, product: ListingRow | null) => void
  onDeleteVariant?: (variant: VariantItem, product: ListingRow | null) => void
  refreshToggle?: boolean
}

const ProductVariantsModal: FC<ProductVariantsModalProps> = ({
  show,
  onClose,
  product,
  isOmsStore,
  platformId,
  goToAddVariant,
  onEditVariant,
  onDeleteVariant,
  refreshToggle,
}) => {
  const dispatch = useAppDispatch()
  console.log('Rendering ProductVariantsModal with product:', product)
  const [loading, setLoading] = useState(false)
  const [variantRows, setVariantRows] = useState<VariantItem[]>([])
  const [tablePage, setTablePage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

useEffect(() => {
  const fetchVariants = async () => {
    if (!show || !product?.id) return

    try {
      setLoading(true)
      setTablePage(1)

      const response = (await dispatch(
        getAllVariantsAsync({
          id: Number(product.id),
          ...(platformId != null && platformId !== '' ? { platform_id: platformId } : {}),
        })
      ).unwrap()) as ProductVariantsApiResponse

      const variants: VariantItem[] = Array.isArray(response?.data?.variants)
        ? response?.data?.variants ?? []
        : []

      setVariantRows(variants)
    } catch (error) {
      console.error('Failed to fetch variants:', error)
      setVariantRows([])
    } finally {
      setLoading(false)
    }
  }

  fetchVariants()
}, [show, product?.id, dispatch, refreshToggle])

  const getOptionValueText = (option: OptionValue) => {
    if (!option) return ''
    return option?.value || option?.api_id || ''
  }

  const getCombinationText = (row: VariantItem) => {
    // const values = [
    // //   getOptionValueText(row?.platform_product_option_value1),
    // //   getOptionValueText(row?.platform_product_option_value2),
    // //   getOptionValueText(row?.platform_product_option_value3),
    // ].filter(Boolean)

    // if (values.length > 0) return values.join(', ')
    // if (row?.name) return row.name
    return '-'
  }

  const getStockValue = (row: VariantItem) => {
    return row?.total_available_quantity ?? 0
  }

  const getPriceValue = (row: VariantItem) => {
    const value = row?.price?.value
    if (value === null || typeof value === 'undefined' || value === '') return '-'

    const num = Number(value)
    if (Number.isNaN(num)) return String(value)

    return `₹ ${num.toFixed(2)}`
  }

  const paginatedRows = useMemo(() => {
    const startIndex = (tablePage - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return variantRows.slice(startIndex, endIndex)
  }, [variantRows, tablePage, rowsPerPage])

  const columns: any = [
    {
      name: 'COMBINATION',
      selector: (row: VariantItem) => getCombinationText(row),
      cell: (row: VariantItem) => (
        // <div className='variantsModal__combination'>{getCombinationText(row)}</div>
        <span className='variantsModal__cellText'>{row?.name || '-'}</span>
      ),
      grow: 2.2,
      minWidth: '220px',
    },
    {
      name: 'SKU CODE',
      selector: (row: VariantItem) => row?.sku || '-',
      cell: (row: VariantItem) => (
        <span className='variantsModal__cellText'>{row?.sku || '-'}</span>
      ),
      grow: 1.3,
      minWidth: '150px',
    },
    {
      name: 'STOCK',
      selector: (row: VariantItem) => getStockValue(row),
      cell: (row: VariantItem) => (
        <span className='variantsModal__cellText'>{getStockValue(row)}</span>
      ),
      width: '100px',
      center: false,
    },
    {
      name: 'PRICE',
      selector: (row: VariantItem) => Number(row?.price?.value ?? 0),
      cell: (row: VariantItem) => (
        <span className='variantsModal__cellText'>{getPriceValue(row)}</span>
      ),
      width: '120px',
      center: false,
    },
    {
      name: 'ACTIONS',
      cell: (row: VariantItem) => (
        <div className='variantsModal__actions'>
          <button
            type='button'
              className="btn btn-sm btn-light-primary"
            title='Edit'
            onClick={() => onEditVariant?.(row, product)}
          >
              <KTIcon iconName="pencil" className="fs-4" />
          </button>

          <button
            type='button'
            className="btn btn-sm btn-light-danger"
            title='Delete'
            onClick={() => onDeleteVariant?.(row, product)}
          >
                <KTIcon iconName="trash" className="fs-4" />
          </button>
        </div>
      ),
      width: '110px',
      right: true,
    },
  ]

  return (
    <Modal
  show={show}
  onHide={onClose}
  centered
  backdrop='static'
  dialogClassName='variantsModal'
  contentClassName='variantsModal__content'
>
      <Modal.Body className='p-0'>
        <div className='variantsModal__wrapper'>
          <div className='variantsModal__header'>
            <div className='variantsModal__headerLeft'>
              <h3 className='variantsModal__title'>Product Variants</h3>
              <div className='variantsModal__subtitle'>
                {product?.name || '-'}
                {product?.sku ? ` (First SKU: ${product.sku})` : ''}
              </div>
            </div>

            <div className='variantsModal__headerRight'>
              <button
                type='button'  style={{ color: '#2563EB', border: '1px solid #2563EB', padding: '6px !important' }}
                title='Add Variant'
                className='btn btn-light variant-btn-table'
                onClick={() => product && goToAddVariant?.(product)}
              >
                <FontAwesomeIcon icon={faPlus} className='me-2' />
                Add New Variant
              </button>

              <button
                type='button'
                className='btn btn-icon variantsModal__closeBtn'
                onClick={onClose}
                title='Close'
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>

          <div className='variantsModal__table'>
            <TableLoadingShell loading={loading} minHeight="50vh">
             <DataTable
  pagination
  paginationServer
  paginationTotalRows={variantRows.length}
  paginationDefaultPage={tablePage}
  paginationPerPage={rowsPerPage}
  onChangePage={(page) => {
    setTablePage(page)
  }}
  onChangeRowsPerPage={(perPage, page) => {
    setRowsPerPage(perPage)
    setTablePage(page)
  }}
  key={`product-variants-table-${tablePage}-${rowsPerPage}-${product?.id || 0}`}
  fixedHeader={true}
  columns={columns}
  noDataComponent={
    <div className='text-secondary'>
      <h5>No records found</h5>
    </div>
  }
  persistTableHead
  progressPending={false}
  data={paginatedRows}
  customStyles={{
    table: {
      style: {
        height: '50vh',
      },
    },
    headCells: {
      style: { fontWeight: 'bold', fontSize: '14px' },
    },
    cells: {
      style: {
        padding: '10px',
        borderBottom: '1px solid gainsboro',
        fontWeight: 'bold',
      },
    },
  }}
/>
            </TableLoadingShell>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default ProductVariantsModal